// DocumentsSection — venue-doc list + upload button + status badges,
// rendered inside the AdminVenues inline-expansion above the existing
// HouseFeedCatalogEditor. Per plan U9.
//
// Status badges trigger different actions:
//   pending/running  → spinner, no actions yet
//   complete         → [Review] opens U10 diff review
//   no_data          → "Couldn't find feed info" + [Delete] primary
//   failed           → tooltip with error_detail + [Retry] (POST .../extract)
//   applied          → "Applied" badge, audit-only
//
// Upload flow:
//   1. Operator picks file
//   2. If venue's prior ack matches current posture → upload directly
//      else → open UploadAckDialog → on confirm → upload
//   3. Upload response branches:
//        201 normal           → trigger /extract, start polling
//        200 duplicate_of     → toast + scroll to prior doc row
//        200/201 near_duplicate_of → open NearDuplicateDialog with 3 options
//        4xx/5xx              → error toast

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, ApiError } from "@/lib/api";
import type {
  DataHandlingPosture,
  NearDuplicateMatch,
  VenueDocument,
  VenueDocumentListResponse,
  VenueDocumentUploadResponse,
} from "@/lib/types";
import { useVenueDocuments } from "@/stores/venueDocuments";
import { UploadAckDialog } from "@/components/UploadAckDialog";

interface DocumentsSectionProps {
  venueId: string;
  posture: DataHandlingPosture | null;
  venueAckPosture: string | null;
  onOpenDiffReview: (documentId: string) => void;
}

const STATUS_LABELS: Record<VenueDocument["extraction_status"], string> = {
  pending: "Queued",
  running: "Extracting…",
  complete: "Ready to review",
  no_data: "No feed info found",
  failed: "Extraction failed",
  applied: "Applied",
};

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

export function DocumentsSection({
  venueId,
  posture,
  venueAckPosture,
  onOpenDiffReview,
}: DocumentsSectionProps) {
  const [showDeleted, setShowDeleted] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingUpload, setPendingUpload] = useState<File | null>(null);
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [nearDupDialog, setNearDupDialog] = useState<
    | { newDocId: string; match: NearDuplicateMatch }
    | null
  >(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const docsByVenue = useVenueDocuments((s) => s.docsByVenue);
  const setDocsForVenue = useVenueDocuments((s) => s.setDocsForVenue);
  const upsertDoc = useVenueDocuments((s) => s.upsertDoc);
  const dropDoc = useVenueDocuments((s) => s.dropDoc);
  const startPolling = useVenueDocuments((s) => s.startPolling);
  const polls = useVenueDocuments((s) => s.polls);

  const docs = useMemo(() => {
    const v = docsByVenue[venueId] || {};
    return Object.values(v)
      .filter((d) => showDeleted || !d.deleted_at)
      .sort((a, b) =>
        (b.uploaded_at || "").localeCompare(a.uploaded_at || ""),
      );
  }, [docsByVenue, venueId, showDeleted]);

  // Initial fetch + refresh on showDeleted change.
  useEffect(() => {
    let cancelled = false;
    api
      .get<VenueDocumentListResponse>(
        `/api/venues/${encodeURIComponent(venueId)}/documents${
          showDeleted ? "?include_deleted=true" : ""
        }`,
      )
      .then((data) => {
        if (cancelled) return;
        setDocsForVenue(venueId, data.documents);
        setLoadError(null);
        // Re-arm polling for any docs still in non-terminal status when
        // we navigate back to the venue.
        for (const d of data.documents) {
          if (
            d.extraction_status === "pending" ||
            d.extraction_status === "running"
          ) {
            startPolling(d.document_id);
          }
        }
      })
      .catch((err: ApiError | unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError
            ? `${err.status}: ${
                (err.body as { error?: string })?.error || "failed"
              }`
            : String(err),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [venueId, showDeleted, setDocsForVenue, startPolling]);

  // ------------------------------------------------------------------
  // Upload pipeline
  // ------------------------------------------------------------------
  const performUpload = useCallback(
    async (
      file: File,
      ack: { acknowledged_posture: string; acknowledged_at: string } | null,
    ) => {
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        if (ack) {
          form.append("acknowledged_posture", ack.acknowledged_posture);
          form.append("acknowledged_at", ack.acknowledged_at);
        }
        const resp = await fetch(
          `/api/venues/${encodeURIComponent(venueId)}/documents`,
          { method: "POST", body: form, credentials: "include" },
        );
        const body: VenueDocumentUploadResponse = await resp.json();
        if (resp.status === 200 && body.duplicate_of) {
          alert(
            `This document was already uploaded ${formatRelative(
              body.uploaded_at || null,
            )}. Viewing the existing row.`,
          );
          // Reload the list — winner may already be present, or we need to fetch.
          const data = await api.get<VenueDocumentListResponse>(
            `/api/venues/${encodeURIComponent(venueId)}/documents${
              showDeleted ? "?include_deleted=true" : ""
            }`,
          );
          setDocsForVenue(venueId, data.documents);
          return;
        }
        if (!resp.ok) {
          alert(
            `Upload failed (${resp.status}): ${
              (body as { error?: string })?.error || "see console"
            }`,
          );
          console.warn("[venue-docs] upload error", resp.status, body);
          return;
        }
        // 201: new doc landed.
        if (body.document_id) {
          // Optimistically insert a pending row.
          const stub: VenueDocument = {
            document_id: body.document_id,
            venue_id: venueId,
            filename: body.filename || file.name,
            content_type: body.content_type || file.type,
            size_bytes: body.size_bytes ?? file.size,
            page_count: body.page_count ?? 0,
            uploaded_by: "you",
            uploaded_at: new Date().toISOString(),
            extraction_status: body.extraction_status || "pending",
            previous_document_id: null,
            pinned_page_indices: [],
            deleted_at: null,
          };
          upsertDoc(stub);

          // Near-duplicate gate: if Layer B fired, surface the dialog
          // instead of immediately triggering extraction. Otherwise
          // trigger /extract directly.
          if (body.near_duplicate_of) {
            setNearDupDialog({
              newDocId: body.document_id,
              match: body.near_duplicate_of,
            });
          } else {
            void triggerExtract(body.document_id);
          }
        }
      } catch (err) {
        alert(`Upload failed: ${err}`);
        console.warn("[venue-docs] upload exception", err);
      } finally {
        setUploading(false);
        setPendingUpload(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [venueId, showDeleted, setDocsForVenue, upsertDoc],
  );

  const triggerExtract = useCallback(
    async (documentId: string, supersedes?: string) => {
      try {
        const qs = supersedes
          ? `?supersedes=${encodeURIComponent(supersedes)}`
          : "";
        await api.post(
          `/api/venue-documents/${encodeURIComponent(documentId)}/extract${qs}`,
        );
        startPolling(documentId);
      } catch (err) {
        // The server transitioned status to 'running'; polling will pick
        // up the eventual 'failed' state.
        if (err instanceof ApiError && err.status === 503) {
          alert("AI is not configured (ANTHROPIC_API_KEY missing). Doc stored only.");
          return;
        }
        console.warn("[venue-docs] extract trigger failed", err);
        startPolling(documentId);
      }
    },
    [startPolling],
  );

  // ------------------------------------------------------------------
  // UI handlers
  // ------------------------------------------------------------------
  const handlePickFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f || !posture) return;
      if (posture.posture === "unverified") {
        alert(
          "Upload disabled: an admin must record Anthropic data-handling " +
            "posture via tools/set_data_handling_posture.py.",
        );
        e.target.value = "";
        return;
      }
      // If the venue already acked under the current posture, skip the dialog.
      if (venueAckPosture === posture.posture) {
        void performUpload(f, null);
        return;
      }
      setPendingUpload(f);
      setAckDialogOpen(true);
    },
    [posture, venueAckPosture, performUpload],
  );

  const handleAckConfirm = useCallback(
    (ack: { acknowledged_posture: string; acknowledged_at: string }) => {
      setAckDialogOpen(false);
      if (!pendingUpload) return;
      void performUpload(pendingUpload, ack);
    },
    [pendingUpload, performUpload],
  );

  const handleAckCancel = useCallback(() => {
    setAckDialogOpen(false);
    setPendingUpload(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleNearDupChoice = useCallback(
    (
      choice: "view_existing" | "fresh_extract" | "supersede",
    ) => {
      const dialog = nearDupDialog;
      if (!dialog) return;
      setNearDupDialog(null);
      if (choice === "view_existing") {
        // Delete the new doc + restore focus to the prior one.
        void api
          .delete(`/api/venue-documents/${encodeURIComponent(dialog.newDocId)}`)
          .catch(() => undefined);
        dropDoc(venueId, dialog.newDocId);
        alert(
          `Existing document is at the top of the list — uploaded ${formatRelative(
            dialog.match.uploaded_at,
          )}.`,
        );
      } else if (choice === "fresh_extract") {
        void triggerExtract(dialog.newDocId);
      } else if (choice === "supersede") {
        void triggerExtract(dialog.newDocId, dialog.match.document_id);
      }
    },
    [nearDupDialog, venueId, dropDoc, triggerExtract],
  );

  const handleDelete = useCallback(
    async (documentId: string) => {
      if (!confirm("Delete this document? (Can be restored from the trash filter.)")) return;
      try {
        await api.delete(
          `/api/venue-documents/${encodeURIComponent(documentId)}`,
        );
        dropDoc(venueId, documentId);
      } catch (err) {
        alert(`Delete failed: ${err}`);
      }
    },
    [venueId, dropDoc],
  );

  const handleReanalyze = useCallback(
    async (documentId: string) => {
      try {
        const resp = await api.post<{
          document_id: string;
          previous_document_id: string;
          extraction_status: string;
        }>(
          `/api/venue-documents/${encodeURIComponent(documentId)}/reanalyze`,
        );
        // Optimistically stash a pending row for the new doc so it
        // appears in the list immediately.
        const stub: VenueDocument = {
          document_id: resp.document_id,
          venue_id: venueId,
          filename: docsByVenue[venueId]?.[documentId]?.filename || "(re-analysis)",
          content_type: docsByVenue[venueId]?.[documentId]?.content_type || "application/pdf",
          size_bytes: docsByVenue[venueId]?.[documentId]?.size_bytes || 0,
          page_count: docsByVenue[venueId]?.[documentId]?.page_count || 0,
          uploaded_by: "you",
          uploaded_at: new Date().toISOString(),
          extraction_status: "pending",
          previous_document_id: documentId,
          pinned_page_indices: [],
          deleted_at: null,
        };
        upsertDoc(stub);
        // Trigger extraction on the new doc + start polling.
        void triggerExtract(resp.document_id);
      } catch (err) {
        if (err instanceof ApiError) {
          const body = err.body as { error?: string; hint?: string };
          alert(
            `Re-analyze failed: ${body.error}${body.hint ? "\n\n" + body.hint : ""}`,
          );
        } else {
          alert(`Re-analyze failed: ${err}`);
        }
      }
    },
    [venueId, docsByVenue, upsertDoc, triggerExtract],
  );

  const handleRestore = useCallback(
    async (documentId: string) => {
      try {
        await api.post(
          `/api/venue-documents/${encodeURIComponent(documentId)}/restore`,
        );
        // Reload the list so the restored doc shows up in the default filter.
        const data = await api.get<VenueDocumentListResponse>(
          `/api/venues/${encodeURIComponent(venueId)}/documents${
            showDeleted ? "?include_deleted=true" : ""
          }`,
        );
        setDocsForVenue(venueId, data.documents);
      } catch (err) {
        alert(`Restore failed: ${err}`);
      }
    },
    [venueId, showDeleted, setDocsForVenue],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="space-y-3 rounded-md border border-border bg-card p-3 text-sm">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Documents</h4>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Show deleted
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.heic,.docx,application/pdf,image/png,image/jpeg,image/heic,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handlePickFile}
            disabled={uploading || !posture || posture.posture === "unverified"}
            className="hidden"
            id={`venue-doc-upload-${venueId}`}
          />
          <Button
            size="sm"
            disabled={uploading || !posture || posture.posture === "unverified"}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Uploading…" : "Upload doc"}
          </Button>
        </div>
      </div>

      {posture?.posture === "unverified" && (
        <p className="rounded bg-destructive/10 p-2 text-xs text-destructive">
          Anthropic data-handling posture is unverified. Uploads disabled until
          an admin records the posture via{" "}
          <code>tools/set_data_handling_posture.py</code> on the cloud-api host.
        </p>
      )}

      {loadError && (
        <p className="rounded bg-destructive/10 p-2 text-xs text-destructive">
          Failed to load documents: {loadError}
        </p>
      )}

      {docs.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No documents yet. Upload a tour-packet PDF to seed the catalog via AI
          extraction.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {docs.map((d) => (
            <DocumentRow
              key={d.document_id}
              doc={d}
              polling={!!polls[d.document_id]}
              onReview={() => onOpenDiffReview(d.document_id)}
              onRetry={() => triggerExtract(d.document_id)}
              onReanalyze={() => handleReanalyze(d.document_id)}
              onDelete={() => handleDelete(d.document_id)}
              onRestore={() => handleRestore(d.document_id)}
            />
          ))}
        </ul>
      )}

      {posture && (
        <UploadAckDialog
          open={ackDialogOpen}
          posture={posture}
          filename={pendingUpload?.name || ""}
          onCancel={handleAckCancel}
          onConfirm={handleAckConfirm}
        />
      )}

      <NearDuplicateDialog
        open={!!nearDupDialog}
        match={nearDupDialog?.match || null}
        onChoice={handleNearDupChoice}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface DocumentRowProps {
  doc: VenueDocument;
  polling: boolean;
  onReview: () => void;
  onRetry: () => void;
  onReanalyze: () => void;
  onDelete: () => void;
  onRestore: () => void;
}

function DocumentRow({
  doc,
  polling,
  onReview,
  onRetry,
  onReanalyze,
  onDelete,
  onRestore,
}: DocumentRowProps) {
  const status = doc.extraction_status;
  const isDeleted = !!doc.deleted_at;

  const statusBadge = (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        status === "complete"
          ? "bg-emerald-500/20 text-emerald-400"
          : status === "applied"
          ? "bg-blue-500/20 text-blue-400"
          : status === "failed"
          ? "bg-destructive/20 text-destructive"
          : status === "no_data"
          ? "bg-amber-500/20 text-amber-500"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {STATUS_LABELS[status]}
      {polling && status !== "complete" && status !== "applied" ? " ⋯" : ""}
    </span>
  );

  return (
    <li
      className={`flex items-center justify-between gap-3 rounded border border-border bg-background px-2 py-1.5 ${
        isDeleted ? "opacity-60" : ""
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="truncate font-medium">{doc.filename}</span>
        <span className="text-xs text-muted-foreground">
          {formatBytes(doc.size_bytes)}
          {doc.page_count > 0 ? ` · ${doc.page_count}p` : ""}
          {doc.uploaded_at ? ` · ${formatRelative(doc.uploaded_at)}` : ""}
        </span>
        {statusBadge}
        {isDeleted && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            deleted
          </span>
        )}
      </div>
      <div className="flex items-center gap-1">
        {!isDeleted && (status === "complete") && (
          <Button size="sm" onClick={onReview}>
            Review
          </Button>
        )}
        {!isDeleted && status === "failed" && (
          <Button size="sm" variant="outline" onClick={onRetry}>
            Retry
          </Button>
        )}
        {!isDeleted &&
          ["complete", "applied", "no_data", "failed"].includes(status) && (
            <Button
              size="sm"
              variant="outline"
              onClick={onReanalyze}
              title="Run AI extraction again on this file (no re-upload). Result lands as a new revision; applying it soft-deletes this prior doc."
            >
              Re-analyze
            </Button>
          )}
        {!isDeleted && (
          <a
            href={`/api/venue-documents/${encodeURIComponent(
              doc.document_id,
            )}/file`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded border border-border px-2 py-1 text-xs hover:bg-accent"
          >
            View
          </a>
        )}
        {!isDeleted ? (
          <Button size="sm" variant="ghost" onClick={onDelete}>
            Delete
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onRestore}>
            Restore
          </Button>
        )}
      </div>
    </li>
  );
}


interface NearDuplicateDialogProps {
  open: boolean;
  match: NearDuplicateMatch | null;
  onChoice: (choice: "view_existing" | "fresh_extract" | "supersede") => void;
}

function NearDuplicateDialog({ open, match, onChoice }: NearDuplicateDialogProps) {
  return (
    <Dialog open={open && !!match} onOpenChange={(o) => !o && onChoice("view_existing")}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Looks like a revision of an existing document</DialogTitle>
          <DialogDescription className="pt-2 text-sm">
            {match && (
              <>
                We detected{" "}
                <strong>{Math.round(match.similarity * 100)}% content overlap</strong>{" "}
                with <em>{match.filename}</em>{" "}
                uploaded {formatRelative(match.uploaded_at)}.
                {match.text_hash_match && (
                  <> The text content is identical — different binary.</>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 text-sm">
          <p className="text-muted-foreground">Pick how to proceed:</p>
        </div>
        <DialogFooter className="flex-col gap-1.5 sm:flex-col sm:gap-1.5">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => onChoice("view_existing")}
          >
            View existing doc — skip AI extraction (no cost)
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => onChoice("fresh_extract")}
          >
            Run fresh extraction — keep both docs side by side
          </Button>
          <Button
            className="w-full justify-start"
            onClick={() => onChoice("supersede")}
          >
            Replace existing as new revision — old doc soft-deletes on apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
